import { useState } from 'react'
import { Lock } from 'lucide-react'
import { formatDate, formatMoney } from '@mavatech/shared'
import { Button, ConfirmDialog, Select } from '@/components/ui'
import { trpc } from '@/lib/trpc'
import { useAppStore } from '@/stores/app.store'

export function UvaTab({ year }: { year: number }) {
  const utils = trpc.useUtils()
  const addToast = useAppStore((s) => s.addToast)
  const cfg = trpc.uva.getConfig.useQuery()
  const [periodNr, setPeriodNr] = useState(new Date().getMonth() + 1)
  const compute = trpc.uva.compute.useQuery({ year, periodNr }, { retry: false })
  const locked = trpc.uva.listLocked.useQuery()
  const [confirmLock, setConfirmLock] = useState(false)

  const lock = trpc.uva.lock.useMutation({
    onSuccess: (r) => {
      utils.uva.compute.invalidate()
      utils.uva.listLocked.invalidate()
      addToast({ type: 'success', title: `Periode ${r.periodKey} gesperrt` })
    },
    onError: (e) => addToast({ type: 'error', title: 'Fehler', description: e.message }),
  })

  const isQuartal = cfg.data?.uvaPeriode === 'quartal'
  const periods = isQuartal
    ? [1, 2, 3, 4].map((n) => ({ value: String(n), label: `Q${n}` }))
    : Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: new Date(year, i, 1).toLocaleDateString('de-AT', { month: 'long' }) }))

  const d = compute.data
  const figs = d ? Object.values(d.figures) : []

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <div className="flex items-end gap-3">
        <Select label={isQuartal ? 'Quartal' : 'Monat'} value={String(periodNr)} onChange={(e) => setPeriodNr(Number(e.target.value))} options={periods} />
        <span className="text-sm text-text-tertiary pb-2">
          {cfg.data?.besteuerungsart === 'ist' ? 'Ist-Besteuerung (Zahlungsdatum)' : 'Soll-Besteuerung (Rechnungsdatum)'}
        </span>
      </div>

      {compute.isLoading || !d ? (
        <p className="text-sm text-text-tertiary">Lädt …</p>
      ) : (
        <div className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-text-primary">UVA {d.periodKey}</p>
            {d.locked ? (
              <span className="text-xs text-success flex items-center gap-1">
                <Lock className="w-3.5 h-3.5" /> gesperrt am {formatDate(d.locked.lockedAt as unknown as string)}
              </span>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setConfirmLock(true)} loading={lock.isPending}>
                <Lock className="w-3.5 h-3.5" /> UVA sperren
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>KZ</th>
                <th>Kennzahl</th>
                <th className="text-right">Betrag</th>
              </tr>
            </thead>
            <tbody>
              {figs.map((f) => (
                <tr key={f.kz} className={f.kz === '095' ? 'font-semibold' : ''}>
                  <td className="font-mono text-text-tertiary">{f.kz}</td>
                  <td className="text-text-secondary">{f.label}</td>
                  <td className="text-right text-money text-text-primary">{formatMoney(f.cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {d.warnings.map((w, i) => (
            <p key={i} className="text-xs text-warning mt-2">
              {w}
            </p>
          ))}
          <p className="text-2xs text-text-tertiary mt-3">
            Diese Aufstellung rechnet und prüft — sie ersetzt keinen Steuerberater und übermittelt nichts.
            Einreichung in FinanzOnline erfolgt manuell.
          </p>
        </div>
      )}

      {!!locked.data?.length && (
        <div>
          <p className="text-sm font-semibold text-text-primary mb-2">Gesperrte Perioden</p>
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Periode</th>
                <th>Gesperrt am</th>
                <th className="text-right">Zahllast</th>
              </tr>
            </thead>
            <tbody>
              {locked.data.map((p) => (
                <tr key={p.id}>
                  <td className="font-mono text-text-secondary">{p.periodKey}</td>
                  <td className="text-text-secondary">{formatDate(p.lockedAt as unknown as string)}</td>
                  <td className="text-right text-money text-text-primary">{formatMoney(p.zahllastCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmLock}
        onOpenChange={setConfirmLock}
        title="UVA-Periode sperren?"
        message="Die Kennzahlen werden als unveränderlicher Schnappschuss gespeichert. Belege und Rechnungen in diesem Zeitraum können danach nicht mehr geändert werden."
        confirmLabel="Sperren"
        onConfirm={() => lock.mutate({ year, periodNr })}
      />
    </div>
  )
}
